/**
 * ProShip Direct v2 — courier details, as a modal over the courier table.
 *
 * A modal rather than a page because the panel's convention for a detail view opened from a
 * table is MUIModal, and because this is a side errand: block a few pincodes, get back to the
 * list, keep your filters and pagination.
 *
 * Blocking is per courier code and per category, because that is how coverage is consumed — a
 * pincode you can deliver to but not collect from is a different fact from one you cannot take
 * COD in. For a Direct courier it reduces whichever coverage applies: their own upload, or
 * ProShip's inherited default.
 *
 * RVP appears only for reverse service types (see categoriesForService): a forward courier code
 * has no reverse coverage to show, and offering it as a blockable category would be a control
 * that does nothing.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Alert, Table, TableBody, TableCell,
  TableHead, TableRow,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import GetAppIcon from '@mui/icons-material/GetApp';

import MUIModal from '../../../common/MUIModal';
import { getUserName } from '../../../helpers/ReduxHelpers';
import { formatDate } from '../../../helpers/UtilityHelper';
import { primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, WARNING, BORDER, LIGHT_BG, SUCCESS } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { MONO } from './directCellStyles';
import { fmtCount } from './directFormat';
import { OwnerChip } from './chips';
import { tHeadSx, tCellSx, tNumSx } from './stats';
import { categoriesForService, parsePincodes } from '../data/serviceabilityStats';
import { downloadCsv } from '../data/serviceabilityUpload';
import { useBlockedPincodes, OWNER } from '../data/hooks';

const labelSx = {
  fontFamily: FONT,
  fontSize: SIZE['4xs'],
  fontWeight: WEIGHT.semibold,
  color: SUBTLE,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  lineHeight: 1.4,
};
const valueSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const subSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE };

/** Every control on the action row is the same height, so they sit on one baseline. */
const CONTROL_HEIGHT = 34;
const fieldSx = {
  '& .MuiInputBase-root': { fontFamily: FONT, fontSize: SIZE['2xs'], height: CONTROL_HEIGHT },
  '& .MuiInputLabel-root': { fontFamily: FONT, fontSize: SIZE['3xs'] },
};

const ACTIONS = [
  { value: 'BLOCK', label: 'Block' },
  { value: 'UNBLOCK', label: 'Unblock' },
];

const TEMPLATE = ['pincode', '110001', '400001', '560001'].join('\n');

function Fact({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

export default function CourierDetailsModal({ courier, onClose }) {
  const { apply, history } = useBlockedPincodes();
  const fileRef = useRef(null);
  const [action, setAction] = useState('BLOCK');
  const [category, setCategory] = useState('lastMile');
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);

  const categories = useMemo(
    () => categoriesForService(courier?.serviceType, courier?.name),
    [courier]
  );

  const courierHistory = useMemo(
    () => (courier ? history.filter((h) => h.code === courier.code).slice(0, 4) : []),
    [history, courier]
  );

  if (!courier) return null;

  const parsed = parsePincodes(text);
  const blockedTotal = categories.reduce((sum, c) => sum + (courier.coverage.blocked[c.key] || 0), 0);
  const activeCategory = categories.some((c) => c.key === category) ? category : categories[0].key;

  const submit = (raw, fileName) => {
    const list = parsePincodes(raw);
    if (list.valid.length === 0) {
      setResult({ severity: 'warning', message: 'No valid pincode found — six digits, not starting with 9.' });
      return;
    }
    apply({
      code: courier.code,
      courierName: `${courier.parent} ${courier.serviceType}`,
      category: activeCategory,
      action,
      pincodes: list.valid,
      fileName,
      user: getUserName(),
    });
    setText('');
    setResult({
      severity: 'success',
      message: `${list.valid.length} ${action === 'BLOCK' ? 'blocked' : 'unblocked'} for ${categories.find((c) => c.key === activeCategory).label}${list.invalid.length ? ` · ${list.invalid.length} rejected` : ''}.`,
    });
  };

  return (
    <MUIModal
      open
      handleClose={onClose}
      maxWidth='sm'
      title={courier.parent}
      action={
        <Button
          size='small'
          sx={secondaryBtnSx}
          startIcon={<DownloadOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={() =>
            downloadCsv(
              `${courier.code}-serviceability.csv`,
              ['pincode,first_mile,last_mile,cod', '110001,yes,yes,yes'].join('\n')
            )
          }
        >
          Download serviceability
        </Button>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
        {/* Identity strip — what you are looking at, in four short facts. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, auto)' },
            gap: GAP.lg,
            p: 1.25,
            bgcolor: LIGHT_BG,
            borderRadius: RADIUS.md,
          }}
        >
          <Fact label='Courier code'>
            <Typography sx={{ ...valueSx, fontFamily: MONO, fontSize: SIZE['3xs'] }}>{courier.code}</Typography>
          </Fact>
          <Fact label='Service type'>
            <Typography sx={valueSx}>{courier.serviceType || '—'}</Typography>
          </Fact>
          <Fact label='Type'>
            <Box sx={{ mt: '1px' }}><OwnerChip owner={courier.owner} /></Box>
          </Fact>
          <Fact label='Status'>
            <Typography sx={{ ...valueSx, color: courier.enabled ? SUCCESS : SUBTLE }}>
              {courier.enabled ? 'Active' : 'Off'}
              {courier.owner === OWNER.DIRECT && courier.coverage.inherited ? ' · ProShip coverage' : ''}
            </Typography>
          </Fact>
        </Box>

        {/* Coverage — one row per category, blocked beside the number it reduces. */}
        <Box>
          <Typography sx={{ ...labelSx, mb: 0.5 }}>Pincode coverage</Typography>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={tHeadSx}>Category</TableCell>
                  <TableCell sx={{ ...tHeadSx, textAlign: 'right' }}>Serviceable</TableCell>
                  <TableCell sx={{ ...tHeadSx, textAlign: 'right' }}>Blocked</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((c, i) => {
                  const blocked = courier.coverage.blocked[c.key] || 0;
                  const last = i === categories.length - 1;
                  return (
                    <TableRow key={c.key} hover>
                      <TableCell sx={{ ...tCellSx, ...(last && { borderBottom: 'none' }) }}>{c.label}</TableCell>
                      <TableCell sx={{ ...tNumSx, fontWeight: WEIGHT.semibold, ...(last && { borderBottom: 'none' }) }}>
                        {fmtCount(courier.coverage[c.key])}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tNumSx,
                          color: blocked ? WARNING : SUBTLE,
                          fontWeight: blocked ? WEIGHT.semibold : WEIGHT.regular,
                          ...(last && { borderBottom: 'none' }),
                        }}
                      >
                        {blocked ? fmtCount(blocked) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          {blockedTotal > 0 && (
            <Typography sx={{ ...subSx, mt: 0.5 }}>
              {fmtCount(blockedTotal)} pincodes blocked by you.
            </Typography>
          )}
        </Box>

        {/* Action row — four controls, one height, one baseline. */}
        <Box>
          <Typography sx={{ ...labelSx, mb: 0.75 }}>Block or unblock</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: GAP.sm, alignItems: 'center' }}>
            <TextField
              select size='small' label='Action' value={action}
              onChange={(e) => setAction(e.target.value)}
              sx={{ ...fieldSx, width: 118 }}
            >
              {ACTIONS.map((a) => (
                <MenuItem key={a.value} value={a.value} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                  {a.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select size='small' label='Category' value={activeCategory}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ ...fieldSx, width: 128 }}
            >
              {categories.map((c) => (
                <MenuItem key={c.key} value={c.key} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size='small'
              placeholder='110001, 400001'
              value={text}
              onChange={(e) => setText(e.target.value)}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: 170,
                '& .MuiInputBase-root': { fontFamily: MONO, fontSize: SIZE['2xs'], height: CONTROL_HEIGHT },
              }}
            />
            <Button
              size='small'
              variant='contained'
              sx={{ ...primaryBtnSx, height: CONTROL_HEIGHT, minWidth: 92 }}
              disabled={parsed.valid.length === 0}
              onClick={() => submit(text)}
            >
              {action === 'BLOCK' ? 'Block' : 'Unblock'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mt: 1, flexWrap: 'wrap' }}>
            <Typography sx={subSx}>
              {text
                ? `${parsed.valid.length} valid${parsed.invalid.length ? ` · ${parsed.invalid.length} rejected` : ''}`
                : 'Paste pincodes, or upload a list'}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button size='small' sx={secondaryBtnSx} startIcon={<GetAppIcon sx={{ fontSize: 15 }} />} onClick={() => downloadCsv('pincode-template.csv', TEMPLATE)}>
              Template
            </Button>
            <Button size='small' sx={secondaryBtnSx} startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 15 }} />} onClick={() => fileRef.current?.click()}>
              Upload CSV
            </Button>
            <input
              ref={fileRef}
              type='file'
              accept='.csv,.txt'
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => submit(String(reader.result || ''), file.name);
                reader.onerror = () => setResult({ severity: 'error', message: `Could not read ${file.name}.` });
                reader.readAsText(file);
                e.target.value = '';
              }}
            />
          </Box>

          {result && (
            <Alert
              severity={result.severity}
              onClose={() => setResult(null)}
              sx={{ mt: 1, fontFamily: FONT, fontSize: SIZE['2xs'], py: 0, alignItems: 'center' }}
            >
              {result.message}
            </Alert>
          )}
        </Box>

        {courierHistory.length > 0 && (
          <Box>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Recent changes</Typography>
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tHeadSx}>When</TableCell>
                    <TableCell sx={tHeadSx}>Source</TableCell>
                    <TableCell sx={tHeadSx}>Change</TableCell>
                    <TableCell sx={tHeadSx}>By</TableCell>
                    <TableCell sx={{ ...tHeadSx, textAlign: 'right' }}>Pincodes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {courierHistory.map((h, i) => {
                    const last = i === courierHistory.length - 1;
                    const edge = last ? { borderBottom: 'none' } : {};
                    return (
                      <TableRow key={h.id} hover>
                        <TableCell sx={{ ...tCellSx, ...edge }}>{formatDate(h.at, 'dd MMM, HH:mm')}</TableCell>
                        <TableCell sx={{ ...tCellSx, ...edge, color: SUBTLE }}>{h.fileName}</TableCell>
                        <TableCell sx={{ ...tCellSx, ...edge }}>
                          <Typography component='span' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: h.action === 'BLOCK' ? WARNING : NAVY }}>
                            {h.action === 'BLOCK' ? 'Blocked' : 'Unblocked'}
                          </Typography>
                          <Typography component='span' sx={{ ...subSx, ml: 0.5 }}>
                            {categories.find((c) => c.key === h.category)?.label || h.category}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tCellSx, ...edge, color: SUBTLE }}>{h.user || '—'}</TableCell>
                        <TableCell sx={{ ...tNumSx, ...edge }}>{fmtCount(h.count)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

      </Box>
    </MUIModal>
  );
}
